<script setup lang="ts">
// The mobile navigation drawer: AppNav presented as a left-anchored sheet on a
// Reka Dialog - the SAME primitive as SidePanel.vue. It inherits focus-trap,
// body scroll-lock, Escape, return-focus, aria-modal, and - crucially -
// content-aware, NESTING-aware outside-tap dismissal: a Reka DropdownMenu opened
// inside it (the account / workspace switcher menus) registers on the shared
// dismissable-layer stack, so tapping a menu item does NOT dismiss the drawer.
// The old hand-rolled drawer re-implemented all of this with global pointer
// listeners and got the nesting wrong (two-tap menus, eaten taps).
//
// Open state lives in the sidebar store (`mobileOpen`); the hamburger in
// AppShell sets it. The content portals to <body>, so the chrome styles are
// global (navdrawer-prefixed), like SidePanel.

import {
  DialogRoot, DialogPortal, DialogOverlay, DialogContent, DialogTitle, VisuallyHidden,
} from '@hq/chat-ui';
import AppNav from '@/components/AppNav.vue';
import { useSidebarStore } from '@/stores/sidebar';
import { useI18n } from '@/lib/i18n';
import type { User } from '@/stores/auth';

defineProps<{ user: User | null }>();

const sidebar = useSidebarStore();
const { t } = useI18n();

// Reka requests open/close (hamburger, Escape, outside-tap) via update:open;
// the store stays the source of truth.
function onOpenChange(next: boolean): void {
  if (next) sidebar.openMobile();
  else sidebar.closeMobile();
}

// Reka auto-focuses the first focusable element on open - the workspace
// switcher - and on iOS that paints a focus-visible ring on it the first time
// the drawer opens (the modality heuristic only suppresses it once a tap has
// happened). Focus the dialog container instead (tabindex=-1, no ring) so the
// focus trap still works but nothing gets a stray border. A real Tab still
// rings the switcher, which is correct for keyboard users.
function onOpenAutoFocus(e: Event): void {
  e.preventDefault();
  // The event fires on the dialog content node; focus it (it's tabindex=-1 with
  // `outline: none`, so no ring) to keep focus trapped without touching items.
  const content = (e.currentTarget ?? e.target) as HTMLElement | null;
  content?.focus();
}
</script>

<template>
  <DialogRoot :open="sidebar.mobileOpen" @update:open="onOpenChange">
    <DialogPortal>
      <!-- Transparent overlay: no dimming (a dim layer read poorly and tangled
           with iOS chrome sampling). It still scroll-locks the background and
           catches an outside tap to dismiss, content-aware, with no lingering
           layer (Reka mounts/unmounts it with the open state). -->
      <DialogOverlay class="navdrawer__overlay" />
      <DialogContent class="navdrawer" :aria-describedby="undefined" @open-auto-focus="onOpenAutoFocus">
        <!-- Reka requires an accessible name for the dialog. -->
        <VisuallyHidden as-child>
          <DialogTitle>{{ t('nav.landmark') }}</DialogTitle>
        </VisuallyHidden>
        <AppNav :user="user" />
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<!-- Unscoped: overlay + content are portalled to <body> by Reka, so a scoped
     hash would never reach them. Classes are `navdrawer`-prefixed. AppNav keeps
     its own scoped styles (incl. its <480 drawer rules: fill height, safe-area
     padding, hidden collapse toggle), which travel with its elements into the
     portal. -->
<style>
.navdrawer__overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  /* No dim. Present only while open. */
}
.navdrawer {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 101;
  box-sizing: border-box;
  width: min(var(--nav-width), 84vw);
  height: 100dvh;
  background: var(--surface-nav);
  box-shadow: var(--shadow-overlay);
  overflow: hidden;
}
.navdrawer:focus-visible { outline: none; }
.navdrawer[data-state="open"]   { animation: navdrawer-in var(--dur-3, 0.22s) var(--ease-out, ease); }
.navdrawer[data-state="closed"] { animation: navdrawer-in var(--dur-2, 0.18s) var(--ease-out, ease) reverse; }

@keyframes navdrawer-in { from { transform: translateX(-100%); } }
@media (prefers-reduced-motion: reduce) {
  .navdrawer[data-state] { animation: none; }
}
</style>
